import { useQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { Fragment, ReactElement, useState } from "react";

import {
  GetActivePricingBundlesQuery,
  GetTenantPricingQuery,
  ListSubaccountsForStaffQuery,
  PricingBundleFieldsFragment,
  SubaccountAssignmentFieldsFragment,
  TenantAssignmentFieldsFragment,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  GET_ACTIVE_PRICING_BUNDLES,
  GET_TENANT_PRICING,
} from "~frontend/graphql/pricingBundle";
import { LIST_SUBACCOUNTS_FOR_STAFF } from "~frontend/graphql/subaccounts";
import { palette } from "~frontend/theme";
import { formatDateUTC } from "~frontend/util/dateFormat";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { ChangePricingDialog } from "./ChangePricingDialog";
import { reasonCodeLabel } from "./helpers";

function PriceTable({
  prices,
}: {
  prices: PricingBundleFieldsFragment["prices"];
}): ReactElement {
  return (
    <TwoColumnDisplay
      labelWidth="200px"
      labelVariant="body3Bold"
      contentVariant="body3"
      rows={prices.map(price => ({
        key: price.id,
        label: price.sku.name,
        content: `$${formatMillicentsAsDollars(price.priceInMillicents)}`,
      }))}
      showTopBorder={false}
    />
  );
}

function AssignmentDetails({
  assignment,
}: {
  assignment:
    | TenantAssignmentFieldsFragment
    | SubaccountAssignmentFieldsFragment;
}): ReactElement {
  const { effectiveDate, expirationDate, createdBy, reasonCode, notes } =
    assignment;
  const start = formatDateUTC(effectiveDate);
  const end = expirationDate ? formatDateUTC(expirationDate) : null;
  return (
    <FlexBox direction="column" gap={0.5}>
      <Typography variant="body3" color="text.secondary">
        Effective: {start} {end && <>– {end}</>}
      </Typography>
      <Typography variant="body3" color="text.secondary">
        Created By: {createdBy?.fullName ?? createdBy?.email}
      </Typography>
      <Typography variant="body3" color="text.secondary">
        Change Reason: {reasonCodeLabel(reasonCode)}
      </Typography>
      {notes && (
        <Typography variant="body3" color="text.secondary">
          Notes: {notes}
        </Typography>
      )}
    </FlexBox>
  );
}

type GroupedSubaccountAssignment = {
  subaccountNames: string[];
  representative: SubaccountAssignmentFieldsFragment;
};

function groupSubaccountAssignments(
  assignments: SubaccountAssignmentFieldsFragment[]
): GroupedSubaccountAssignment[] {
  const groups = new Map<string, GroupedSubaccountAssignment>();
  for (const assignment of assignments) {
    const key = [
      assignment.pricingBundle.id,
      assignment.effectiveDate,
      assignment.expirationDate ?? "",
      assignment.reasonCode,
      assignment.notes ?? "",
    ].join("|");
    const existing = groups.get(key);
    if (existing) {
      existing.subaccountNames.push(assignment.subaccountName);
    } else {
      groups.set(key, {
        subaccountNames: [assignment.subaccountName],
        representative: assignment,
      });
    }
  }
  return Array.from(groups.values());
}

function SubaccountAssignmentCard({
  subaccountNames,
  assignment,
}: {
  subaccountNames: string[];
  assignment: SubaccountAssignmentFieldsFragment;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const assignmentIdSuffix = [
    assignment.pricingBundle.id,
    assignment.effectiveDate,
    assignment.expirationDate ?? "",
    assignment.reasonCode,
    assignment.notes ?? "",
  ]
    .map(value => encodeURIComponent(value))
    .join("-");
  const detailsId = `subaccount-pricing-details-${assignmentIdSuffix}`;
  const summaryId = `subaccount-pricing-summary-${assignmentIdSuffix}`;

  return (
    <Accordion
      data-testid="subaccount-pricing-card"
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        marginBottom: 1,
        background: "none",
        p: 0,
        border: `1px solid ${palette.lightGray}`,
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        id={summaryId}
        aria-controls={detailsId}
        expandIcon={
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              fontSize: "14px",
            }}
          />
        }
        sx={{
          minHeight: "28px",
          backgroundColor: palette.lightGray,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
          "&.Mui-expanded": {
            minHeight: "28px",
          },
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <Typography variant="body2Bold" data-testid="subaccount-card-header">
          {subaccountNames.join(", ")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id={detailsId} sx={{ py: 1 }}>
        <Typography variant="body2Bold">
          {assignment.pricingBundle.name}
        </Typography>
        <AssignmentDetails assignment={assignment} />
        <Box sx={{ pt: 2 }}>
          <PriceTable prices={assignment.pricingBundle.prices} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function SubaccountAssignmentAccordions({
  assignments,
  heading,
  description,
}: {
  assignments: SubaccountAssignmentFieldsFragment[];
  heading: string;
  description?: string;
}): ReactElement {
  const grouped = groupSubaccountAssignments(assignments);
  return (
    <FlexBox direction="column" gap={3}>
      <Box>
        <Typography variant="h2Bold" sx={{ mb: 0.5, fontWeight: 600 }}>
          {heading}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        )}
      </Box>
      {grouped.map(group => (
        <SubaccountAssignmentCard
          key={group.representative.id}
          subaccountNames={group.subaccountNames}
          assignment={group.representative}
        />
      ))}
    </FlexBox>
  );
}

function AssignmentPriceSection({
  assignment,
}: {
  assignment: TenantAssignmentFieldsFragment;
}): ReactElement {
  return (
    <Box>
      <FlexBox gap={1} alignItems="center">
        <Typography variant="body2Bold" fontWeight="medium">
          {assignment.pricingBundle.name}
        </Typography>
      </FlexBox>
      <Box sx={{ pb: 1 }}>
        <AssignmentDetails assignment={assignment} />
      </Box>
      <PriceTable prices={assignment.pricingBundle.prices} />
    </Box>
  );
}

function CurrentPricingSection({
  currentAssignment,
  currentSubaccountAssignments,
}: {
  currentAssignment: TenantAssignmentFieldsFragment;
  currentSubaccountAssignments: SubaccountAssignmentFieldsFragment[];
}): ReactElement {
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [subaccountDialogOpen, setSubaccountDialogOpen] = useState(false);
  const anyDialogOpen = changeDialogOpen || subaccountDialogOpen;
  const { data: bundlesData } = useQuery<GetActivePricingBundlesQuery>(
    GET_ACTIVE_PRICING_BUNDLES,
    { skip: !anyDialogOpen }
  );
  const { data: subaccountsData } = useQuery<ListSubaccountsForStaffQuery>(
    LIST_SUBACCOUNTS_FOR_STAFF,
    { skip: !subaccountDialogOpen }
  );

  const activeSubaccounts = (subaccountsData?.listSubaccounts ?? [])
    .filter(sa => sa.archivedAt == null)
    .map(sa => ({
      id: sa.id,
      name: sa.name,
      slug: sa.slug,
      isWeText: sa.isWeText,
    }));

  return (
    <FlexBox
      direction="column"
      gap={5}
      sx={{ backgroundColor: palette.white, px: 5, py: 3 }}
    >
      <FlexBox justifyContent="space-between" alignItems="flex-start">
        <Typography variant="h2Bold" sx={{ fontWeight: 600 }}>
          Current Pricing
        </Typography>
        <Box>
          <FlexBox gap={1}>
            <Button
              variant="contained"
              color="tertiary"
              onClick={() => setSubaccountDialogOpen(true)}
              sx={{
                fontSize: "10px",
                letterSpacing: "0.4px",
                px: 1,
                py: 0.75,
                height: "30px",
                fontWeight: 500,
              }}
            >
              + Add Subaccount Specific Pricing
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setChangeDialogOpen(true)}
              sx={{
                fontSize: "10px",
                letterSpacing: "0.4px",
                px: 1,
                py: 0.75,
                height: "30px",
                fontWeight: 500,
              }}
            >
              Change Pricing
            </Button>
          </FlexBox>
        </Box>
      </FlexBox>

      <AssignmentPriceSection assignment={currentAssignment} />

      {currentSubaccountAssignments.length > 0 && (
        <SubaccountAssignmentAccordions
          assignments={currentSubaccountAssignments}
          heading="Current Subaccount Specific Pricing"
          description="Any subaccounts not shown here will use the pricing assigned to the organization above."
        />
      )}

      <ChangePricingDialog
        open={changeDialogOpen}
        onClose={() => setChangeDialogOpen(false)}
        bundles={bundlesData?.getActivePricingBundles ?? []}
        currentBundleId={currentAssignment.pricingBundle.id}
      />
      <ChangePricingDialog
        open={subaccountDialogOpen}
        onClose={() => setSubaccountDialogOpen(false)}
        mode="subaccount"
        bundles={bundlesData?.getActivePricingBundles ?? []}
        subaccounts={activeSubaccounts}
      />
    </FlexBox>
  );
}

function AssignmentListSection({
  tenantAssignments,
  subaccountAssignments,
  title,
}: {
  tenantAssignments: TenantAssignmentFieldsFragment[];
  subaccountAssignments: SubaccountAssignmentFieldsFragment[];
  title: string;
}): ReactElement {
  const hasContent =
    tenantAssignments.length > 0 || subaccountAssignments.length > 0;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ backgroundColor: palette.white, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h2Bold">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FlexBox direction="column" gap={2}>
          {!hasContent && (
            <Typography variant="body2" color="text.secondary">
              No assignments.
            </Typography>
          )}
          {tenantAssignments.map(assignment => (
            <Fragment key={assignment.id}>
              <AssignmentPriceSection assignment={assignment} />
            </Fragment>
          ))}
          {subaccountAssignments.length > 0 && (
            <SubaccountAssignmentAccordions
              assignments={subaccountAssignments}
              heading="Subaccount Specific Pricing"
            />
          )}
        </FlexBox>
      </AccordionDetails>
    </Accordion>
  );
}

export function PricingSection(): ReactElement {
  const { data, loading, error } =
    useQuery<GetTenantPricingQuery>(GET_TENANT_PRICING);

  return (
    <Box sx={{ mt: 4 }}>
      <FlexBox>
        <Typography variant="h0Bold" component="h2" sx={{ mb: 2 }}>
          Pricing
        </Typography>
        <Box>
          <FlexBox
            justifyContent="center"
            sx={{
              px: 0.5,
              py: 0,
              mt: 1.5,
              mb: "2px",
              ml: 1,
              backgroundColor: palette.midGray,
            }}
          >
            <Typography
              variant="body3Bold"
              color={palette.navy}
              component="span"
            >
              Staff Setting
            </Typography>
          </FlexBox>
        </Box>
      </FlexBox>
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ getTenantPricing }) => (
          <FlexBox direction="column" gap={2}>
            <CurrentPricingSection
              currentAssignment={getTenantPricing.currentAssignment}
              currentSubaccountAssignments={
                getTenantPricing.currentSubaccountAssignments
              }
            />
            <AssignmentListSection
              title="Future Pricing"
              tenantAssignments={getTenantPricing.futureAssignments}
              subaccountAssignments={
                getTenantPricing.futureSubaccountAssignments
              }
            />
            <AssignmentListSection
              title="Pricing History"
              tenantAssignments={getTenantPricing.pastAssignments}
              subaccountAssignments={getTenantPricing.pastSubaccountAssignments}
            />
          </FlexBox>
        )}
      </AsyncWrapper>
    </Box>
  );
}
